import classNames from 'classnames';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DEPTH, CREATE_ORDER_SIDE } from 'src/common/constants';
import { currencyFormatter, DateTimeFormatter } from 'src/common/helpers';
import { StyledTable } from 'src/components/features/common/table/styledTable';
import { useAppSelector } from 'src/hooks';
import { Symbol, MyTrade, OrderTableContentProps } from 'src/types';

const TradeHistoryTable: FC<OrderTableContentProps> = ({ isShowAll }) => {
    const { t } = useTranslation();
    const [filterTradeHistory, setFilterTradeHistory] = useState([] as Array<MyTrade>);
    const symbol: Symbol = useAppSelector((state) => state.spotPage.currentSymbol);
    const symbolId = symbol.symbolId;
    const myTrades: Array<MyTrade> = useAppSelector((state) => state.spotPage.myTrades);
    const symbols = useAppSelector((state) => state.globalData.configV2.symbol);

    useEffect(() => {
        let filtered = isShowAll
            ? myTrades
            : myTrades.filter((myTrade) => myTrade.symbolId === symbolId);
        filtered = [...filtered].sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
        setFilterTradeHistory(filtered);
    }, [myTrades, symbolId, isShowAll]);

    const columns = [
        {
            title: t('spot_pairs'),
            key: 'spotPairs',
            width: '5%',
            render: (order: MyTrade) => {
                return (
                    <span>
                        {order.baseTokenId}
                        <span className="greyText">/{order.quoteTokenName}</span>
                    </span>
                );
            },
        },
        {
            title: t('order_type'),
            key: 'orderType',
            width: '4%',
            render: (order: MyTrade) => {
                return <span>{t(order.type.toLowerCase())}</span>;
            },
        },
        {
            title: t('direction'),
            key: 'direction',
            width: '4%',
            render: (order: MyTrade) => {
                return (
                    <span
                        className={classNames(
                            {
                                greenText: order.side === CREATE_ORDER_SIDE.BUY,
                            },
                            {
                                redText: order.side === CREATE_ORDER_SIDE.SELL,
                            },
                        )}
                    >
                        {t(order.side.toLowerCase())}
                    </span>
                );
            },
        },
        {
            title: t('filled_value'),
            key: 'filledValue',
            width: '6%',
            render: (order: MyTrade) => {
                const orderValue = order.price * order.quantity;
                return (
                    <span>
                        {currencyFormatter(
                            orderValue,
                            DEPTH[symbols[order.symbolId].quotePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('filled_price'),
            key: 'filledPrice',
            width: '6%',
            render: (order: MyTrade) => {
                return (
                    <span>
                        {currencyFormatter(
                            order.price,
                            DEPTH[symbols[order.symbolId].minPricePrecision],
                        )}{' '}
                        <span className="greyText">{order.quoteTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('filled_qty'),
            key: 'filledQty',
            width: '5%',
            render: (order: MyTrade) => {
                return (
                    <span>
                        {currencyFormatter(
                            order.quantity,
                            DEPTH[symbols[order.symbolId].basePrecision],
                        )}{' '}
                        <span className="greyText">{order.baseTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('trading_fee'),
            key: 'tradingFee',
            width: '7%',
            render: (order: MyTrade) => {
                return (
                    <span>
                        {order.fee} <span className="greyText">{order.feeTokenId}</span>
                    </span>
                );
            },
        },
        {
            title: t('filled_time'),
            key: 'filledTime',
            width: '7%',
            render: (order: MyTrade) => {
                return <span>{DateTimeFormatter(Number(order.time))}</span>;
            },
        },
        {
            title: t('transaction_id'),
            key: 'transactionID',
            width: '8%',
            render: (order: MyTrade) => {
                return <span>{order.tradeId}</span>;
            },
        },
        {
            title: t('order_id'),
            key: 'orderID',
            width: '8%',
            render: (order: MyTrade) => {
                return <span>{order.orderId}</span>;
            },
        },
    ];

    return (
        <StyledTable
            style={{ minWidth: '1529px' }}
            columns={columns}
            data={filterTradeHistory}
            rowKey="tradeId"
        />
    );
};

export { TradeHistoryTable };
